import React, { useCallback, useState, useEffect, useRef, useId } from 'react';
import { Clock, X, ChevronUp, ChevronDown, XCircle, Eraser, ChevronRight, KeyRound } from 'lucide-react';
import '../styles/UploadTray.css';
import { useUploads } from './UploadManagerContext';
import archiveImportService from '../services/archiveImportService';
import ArchivePasswordRetryModal from './ArchivePasswordRetryModal';
import {
  ARCHIVE_IMPORT_TERMINAL_EVENT,
  archiveImportJobFromResponse,
  resolveArchivePasswordRetry,
} from '../utils/archivePasswordRetry';

const ARCHIVE_TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

const waitForArchivePoll = (delayMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(new DOMException('Archive status polling was cancelled.', 'AbortError'));
    return;
  }
  let timer = null;
  const handleAbort = () => {
    clearTimeout(timer);
    reject(new DOMException('Archive status polling was cancelled.', 'AbortError'));
  };
  timer = setTimeout(() => {
    signal?.removeEventListener('abort', handleAbort);
    resolve();
  }, delayMs);
  signal?.addEventListener('abort', handleAbort, { once: true });
});

export const archiveRetryUploadPatch = (job, upload = {}) => {
  const status = String(job?.status || '').toLowerCase();
  const numericProgress = Number(job?.progress_percent ?? job?.progress ?? upload?.progress ?? 0);
  const progress = Math.max(0, Math.min(100, Number.isFinite(numericProgress) ? numericProgress : 0));
  const importMode = job?.import_mode || upload?.archiveImportMode || 'prepare';
  const statusMessage = job?.status_message || (
    status === 'validating' ? 'Checking the saved document package' :
    status === 'extracting' ? 'Organizing the saved documents' :
    status === 'publishing' ? 'Adding documents to the case' :
    status === 'completed' ? (importMode === 'archive_only' ? 'Original files uploaded' : 'Documents are ready') :
    status === 'failed' ? 'The saved package needs attention' :
    'Waiting to organize the saved package'
  );

  return {
    archiveJobId: String(job?.job_id || job?.public_id || upload?.archiveJobId || ''),
    archivePasswordRetry: resolveArchivePasswordRetry(job),
    progress,
    status: status === 'completed' ? 'complete' : status === 'failed' ? 'failed' : status === 'cancelled' ? 'cancelled' : 'uploading',
    statusMessage,
    error: status === 'failed'
      ? (job?.error || job?.message || job?.public_error_message || 'The document package could not be prepared.')
      : null,
  };
};

const archiveRetryRequestError = (error) => {
  switch (String(error?.code || '')) {
    case 'archive_password_too_long':
      return 'That ZIP password is too long.';
    case 'archive_password_unsafe':
      return 'That ZIP password contains unsupported characters.';
    case 'archive_password_retry_stale':
      return 'The saved package changed. Its current retry status has been refreshed.';
    case 'archive_password_retry_unavailable':
      return 'This saved package is no longer available for a password retry.';
    case 'archive_password_protection_failed':
      return 'Secure password handling is temporarily unavailable. Please try again.';
    default:
      return 'The saved package could not be resumed. Re-enter the ZIP password and try again.';
  }
};

export const getUploadTrayBatchSummaryCount = (batch) => {
  const declaredTotal = Number(batch?.totalFiles || 0);
  return declaredTotal > 0 ? declaredTotal : (batch?.files?.length || 0);
};

/**
 * Format S3 file path to be more user-friendly
 * Replace storage placeholders for display only, including relative roots.
 */
const formatFilePath = (filePath, caseName) => {
  if (!filePath) return '';
  
  return String(filePath)
    .replace(/^cases\/\d+\/(?=\{\{Name\}\}(?:\/|$))/, '')
    .replace(/\{\{Name\}\}/g, () => caseName || 'Case documents');
};

const UploadOutcomeGroup = ({ label, uploads, renderUpload }) => {
  const [expanded, setExpanded] = useState(false);
  const groupId = useId();
  if (uploads.length === 0) return null;

  return (
    <section className="upload-outcome-group">
      <button
        id={`${groupId}-toggle`}
        type="button"
        className="action quaternary upload-outcome-toggle"
        aria-expanded={expanded}
        aria-controls={`${groupId}-items`}
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
        <span className="upload-outcome-label">{label}</span>
        <span className="upload-outcome-count">{uploads.length.toLocaleString()}</span>
      </button>
      <div
        id={`${groupId}-items`}
        role="region"
        aria-labelledby={`${groupId}-toggle`}
        className="upload-outcome-items"
        hidden={!expanded}
      >
        {expanded && uploads.map(renderUpload)}
      </div>
    </section>
  );
};

export const UploadTray = ({ uploads }) => {
  const [minimized, setMinimized] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [passwordRetryTargetId, setPasswordRetryTargetId] = useState(null);
  const [passwordRetryBusy, setPasswordRetryBusy] = useState(false);
  const [passwordRetryError, setPasswordRetryError] = useState('');
  const [cancellingUploadIds, setCancellingUploadIds] = useState(() => new Set());
  const {
    clearCompletedUploads,
    clearAllUploads,
    dispatch,
    toggleBatchExpand,
    toggleSuperBatchExpand,
    toggleSubBatchExpand,
  } = useUploads();
  const uploadsRef = useRef(uploads || {});
  const dismissedUploadIdsRef = useRef(new Set());
  const promptedRetryTokensRef = useRef(new Set());
  const archiveRetryPollsRef = useRef(new Map());
  
  const files = Object.values(uploads || {});
  const passwordRetryTarget = passwordRetryTargetId ? uploads?.[passwordRetryTargetId] : null;
  uploadsRef.current = uploads || {};

  const publishArchiveTerminal = useCallback((upload, job) => {
    if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent(ARCHIVE_IMPORT_TERMINAL_EVENT, {
      detail: {
        caseId: job?.case_id || upload?.caseId || null,
        destinationPath: job?.destination_path || upload?.archiveDestinationPath || '',
        jobId: job?.job_id || job?.public_id || upload?.archiveJobId || '',
        status: String(job?.status || '').toLowerCase(),
      },
    }));
  }, []);

  const pollResumedArchive = useCallback(async (uploadId, jobId) => {
    const existing = archiveRetryPollsRef.current.get(uploadId);
    existing?.abort();
    const controller = new AbortController();
    archiveRetryPollsRef.current.set(uploadId, controller);
    let failureCount = 0;
    let pollCount = 0;

    try {
      while (!controller.signal.aborted && uploadsRef.current?.[uploadId]) {
        let response;
        try {
          response = await archiveImportService.status(jobId, { signal: controller.signal });
          failureCount = 0;
        } catch (error) {
          if (error?.name === 'AbortError') return null;
          const retryable = !error?.status || error.status >= 500;
          if (!retryable) {
            dispatch({
              type: 'UPDATE_UPLOAD',
              payload: {
                id: uploadId,
                archivePasswordRetry: null,
                error: 'CaseDB could not check the resumed document package.',
                status: 'failed',
                statusMessage: 'Document package status unavailable',
              },
            });
            return null;
          }
          failureCount += 1;
          await waitForArchivePoll(Math.min(30000, 2000 * failureCount), controller.signal);
          continue;
        }

        const job = archiveImportJobFromResponse(response);
        const patch = archiveRetryUploadPatch(job, uploadsRef.current?.[uploadId]);
        dispatch({ type: 'UPDATE_UPLOAD', payload: { id: uploadId, ...patch } });
        const status = String(job?.status || '').toLowerCase();
        if (ARCHIVE_TERMINAL_STATUSES.has(status)) {
          publishArchiveTerminal(uploadsRef.current?.[uploadId], job);
          return job;
        }

        pollCount += 1;
        const delayMs = pollCount < 20 ? 3000 : pollCount < 74 ? 10000 : 30000;
        await waitForArchivePoll(delayMs, controller.signal);
      }
      return null;
    } catch (error) {
      if (error?.name !== 'AbortError' && uploadsRef.current?.[uploadId]) {
        dispatch({
          type: 'UPDATE_UPLOAD',
          payload: {
            id: uploadId,
            archivePasswordRetry: null,
            error: 'CaseDB could not check the resumed document package.',
            status: 'failed',
            statusMessage: 'Document package status unavailable',
          },
        });
      }
      return null;
    } finally {
      if (archiveRetryPollsRef.current.get(uploadId) === controller) {
        archiveRetryPollsRef.current.delete(uploadId);
      }
    }
  }, [dispatch, publishArchiveTerminal]);

  const reconcileArchiveRetryStatus = useCallback(async (uploadId, jobId) => {
    try {
      const response = await archiveImportService.status(jobId);
      const job = archiveImportJobFromResponse(response);
      const patch = archiveRetryUploadPatch(job, uploadsRef.current?.[uploadId]);
      dispatch({ type: 'UPDATE_UPLOAD', payload: { id: uploadId, ...patch } });
      return patch.archivePasswordRetry;
    } catch (error) {
      return uploadsRef.current?.[uploadId]?.archivePasswordRetry || null;
    }
  }, [dispatch]);

  const submitArchivePasswordRetry = useCallback(async (password) => {
    if (passwordRetryBusy) return;
    const uploadId = passwordRetryTargetId;
    const upload = uploadsRef.current?.[uploadId];
    const retry = upload?.archivePasswordRetry;
    if (!uploadId || !retry?.jobId || !retry?.retryToken) {
      setPasswordRetryError('Refresh the package status before trying again.');
      return;
    }

    setPasswordRetryBusy(true);
    setPasswordRetryError('');
    try {
      const response = await archiveImportService.retryWithPassword({
        jobId: retry.jobId,
        retryToken: retry.retryToken,
        password,
      });
      const job = archiveImportJobFromResponse(response);
      const patch = archiveRetryUploadPatch(job, upload);
      dispatch({ type: 'UPDATE_UPLOAD', payload: { id: uploadId, ...patch } });
      setPasswordRetryTargetId(null);

      const status = String(job?.status || '').toLowerCase();
      if (ARCHIVE_TERMINAL_STATUSES.has(status)) {
        publishArchiveTerminal(upload, job);
      } else {
        void pollResumedArchive(uploadId, retry.jobId);
      }
    } catch (error) {
      setPasswordRetryError(archiveRetryRequestError(error));
      if (['archive_password_retry_stale', 'archive_password_retry_unavailable'].includes(String(error?.code || ''))) {
        const currentRetry = await reconcileArchiveRetryStatus(uploadId, retry.jobId);
        if (!currentRetry) setPasswordRetryTargetId(null);
      }
    } finally {
      setPasswordRetryBusy(false);
    }
  }, [passwordRetryBusy, passwordRetryTargetId, dispatch, pollResumedArchive, publishArchiveTerminal, reconcileArchiveRetryStatus]);

  const closePasswordRetry = useCallback(() => {
    if (passwordRetryBusy) return;
    setPasswordRetryError('');
    setPasswordRetryTargetId(null);
  }, [passwordRetryBusy]);

  const cancelBackgroundUpload = useCallback(async (upload) => {
    const uploadId = String(upload?.id || '');
    if (!uploadId || !upload?.cancelUrl || cancellingUploadIds.has(uploadId)) return;
    setCancellingUploadIds(current => new Set(current).add(uploadId));
    dispatch({
      type: 'UPDATE_UPLOAD',
      payload: { id: uploadId, statusMessage: 'Cancelling...', error: null },
    });
    try {
      const response = await fetch(upload.cancelUrl, {
        method: upload.cancelMethod || 'POST',
        credentials: 'include',
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || 'CaseDB could not cancel this operation.');
      }
      dispatch({
        type: 'UPDATE_UPLOAD',
        payload: { id: uploadId, status: 'cancelled', statusMessage: 'Cancelled', error: null },
      });
    } catch (error) {
      dispatch({
        type: 'UPDATE_UPLOAD',
        payload: {
          id: uploadId,
          status: 'uploading',
          statusMessage: 'Still running',
          error: error?.message || 'CaseDB could not cancel this operation.',
        },
      });
    } finally {
      setCancellingUploadIds(current => {
        const next = new Set(current);
        next.delete(uploadId);
        return next;
      });
    }
  }, [cancellingUploadIds, dispatch]);

  useEffect(() => () => {
    archiveRetryPollsRef.current.forEach((controller) => controller.abort());
    archiveRetryPollsRef.current.clear();
  }, []);

  useEffect(() => {
    const activeTokens = new Set();
    files.forEach((file) => {
      const retry = file?.archivePasswordRetry;
      if (retry?.retryToken) activeTokens.add(retry.retryToken);
    });
    promptedRetryTokensRef.current.forEach((token) => {
      if (!activeTokens.has(token)) promptedRetryTokensRef.current.delete(token);
    });

    if (passwordRetryTargetId || passwordRetryBusy) return;
    const nextUpload = files.find((file) => {
      const retry = file?.archivePasswordRetry;
      return file?.operation === 'archive_import'
        && file?.status === 'failed'
        && retry?.jobId
        && retry?.retryToken
        && !promptedRetryTokensRef.current.has(retry.retryToken);
    });
    if (!nextUpload) return;
    promptedRetryTokensRef.current.add(nextUpload.archivePasswordRetry.retryToken);
    setPasswordRetryError('');
    setPasswordRetryTargetId(nextUpload.id);
  }, [files, passwordRetryBusy, passwordRetryTargetId]);

  const totalFilesRef = useRef(0);
  useEffect(() => {
    const currentCount = files.length;

    if (userDismissed && files.some(file => !dismissedUploadIdsRef.current.has(file.id))) {
      dismissedUploadIdsRef.current.clear();
      setUserDismissed(false);
    }
    
    if (currentCount > totalFilesRef.current) {
      totalFilesRef.current = currentCount;
    }
    
    if (currentCount === 0) {
      totalFilesRef.current = 0;
      dismissedUploadIdsRef.current.clear();
      setUserDismissed(false); // Reset dismissal when all items are gone
    }
  }, [files, userDismissed]);

  useEffect(() => {
    if (files.length > 0 && !userDismissed) {
      setVisible(true);
    } else if (visible && files.length === 0) {
      const timer = setTimeout(() => setVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [files.length, visible, userDismissed]);

  if (!visible) return null;

  const overallProgress = files.length 
    ? Math.round(files.reduce((sum, f) => sum + (f.progress || 0), 0) / files.length) 
    : 0;
    
  const finishedUploads = files.filter(f => f.status === 'complete').length;
  const skippedUploads = files.filter(f => f.status === 'complete' && f.skipped === true).length;
  const completedUploads = finishedUploads - skippedUploads;
  const failedUploads = files.filter(f => f.status === 'failed').length;
  const cancelledUploads = files.filter(f => f.status === 'cancelled').length;
  const inProgressUploads = files.filter(f => f.status === 'uploading').length;
  
  // Calculate total items
  // Super batches count as 1 item regardless of internal batch count
  // Regular batches also count as 1
  // Single operations count as 1
  const totalFiles = files.length || totalFilesRef.current;
  
  let headerText = 'No files uploading';
  if (inProgressUploads > 0) {
    headerText = `Processing ${finishedUploads} of ${totalFiles}... ${overallProgress}%`;
  } else if (skippedUploads > 0 && failedUploads === 0) {
    headerText = `${completedUploads ? `${completedUploads} completed, ` : ''}${skippedUploads} skipped — already ${skippedUploads === 1 ? 'exists' : 'exist'}`;
  } else if (completedUploads > 0 && failedUploads === 0) {
    headerText = `${completedUploads} operation${completedUploads > 1 ? 's' : ''} completed successfully`;
  } else if (failedUploads > 0) {
    headerText = `${failedUploads} operation${failedUploads > 1 ? 's' : ''} failed`;
  } else if (cancelledUploads > 0) {
    headerText = `${cancelledUploads} operation${cancelledUploads > 1 ? 's' : ''} cancelled`;
  }

  const handleClearCompleted = (e) => {
    e.stopPropagation();
    clearCompletedUploads();
  };

  const renderUpload = (file, index) => {
    // Super Batch item (consolidates all sub-batches)
    if (file.isSuperBatch) {
      const totalFiles = file.totalFiles || 0;
      const totalBatches = file.totalBatches || 0;
      const completedBatches = file.completedBatches || 0;
      const processedFiles = file.processedFiles || 0;
      const batches = file.batches || [];

      const operationLabel = file.operation === 'delete' ? 'Deleting' :
                            file.operation === 'move' ? 'Moving' :
                            file.operation === 'copy' ? 'Copying' :
                            file.operation === 'undo' ? 'Undoing' :
                            file.operation === 'upload' ? 'Uploading' : 'Processing';

      return (
        <div key={file.id || index} className={`upload-item super-batch ${file.status}`}>
          <div
            className="upload-item-info super-batch-header"
            onClick={() => toggleSuperBatchExpand(file.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="batch-title">
              <ChevronRight
                size={18}
                style={{
                  transform: file.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
              <span className="file-name">
                {operationLabel} files
              </span>
            </div>
            <span className="file-status small-text" style={{ color: 'var(--subtext-color)' }}>
              {file.status === 'uploading' ?
                `${processedFiles.toLocaleString()}/${totalFiles.toLocaleString()} • ${file.progress || 0}%` :
               file.status === 'complete' ?
                `${totalFiles.toLocaleString()} files completed` :
                `Failed`}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${file.progress || 0}%`,
                backgroundColor: file.status === 'failed' ? '#f44336' :
                                file.status === 'complete' ? '#4caf50' : '#2196f3'
              }}
            ></div>
          </div>

          {/* Expanded: Show all sub-batches */}
          {file.expanded && batches.length > 0 && (
            <div className="super-batch-details">
              <div className="sub-batches-header">
                {completedBatches}/{totalBatches} batches completed
              </div>
              {batches.map((subBatch, subIndex) => (
                <div key={subIndex} className={`sub-batch-item ${subBatch.status}`}>
                  <div
                    className="sub-batch-header"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSubBatchExpand(file.id, subBatch.id);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="sub-batch-title">
                      <ChevronRight
                        size={14}
                        style={{
                          transform: subBatch.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      />
                      <span>Batch {subBatch.batchNumber}/{totalBatches}</span>
                    </div>
                    <span className="sub-batch-status">
                      {subBatch.totalFiles} files • {subBatch.status === 'complete' ? 'Complete' : subBatch.status === 'failed' ? 'Failed' : `${subBatch.progress}%`}
                    </span>
                  </div>

                  {/* Expanded sub-batch: Show files */}
                  {subBatch.expanded && subBatch.files && subBatch.files.length > 0 && (
                    <div className="sub-batch-files">
                      {subBatch.files.slice(0, 100).map((f, fIdx) => (
                        <div key={fIdx} className="sub-batch-file">
                          <span className="file-name-small">{formatFilePath(f.name || f.path, f.caseName)}</span>
                          {f.status && f.status !== 'pending' && (
                            <span className={`file-status-badge ${f.status}`}>
                              {f.status}
                            </span>
                          )}
                        </div>
                      ))}
                      {subBatch.files.length > 100 && (
                        <div className="sub-batch-file-more">
                          + {subBatch.files.length - 100} more files...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {file.error &&
            <div className='error-message'>
              <span>{file.error}</span>
            </div>
          }
        </div>
      );
    }

    // Regular Batch item (for operations that don't use super batches)
    if (file.isBatch) {
      const totalFiles = getUploadTrayBatchSummaryCount(file);
      const currentBatch = file.currentBatch || 1;
      const totalBatches = file.totalBatches || 1;

      // The declared total remains authoritative even when detailed
      // rows are capped for tray rendering performance.
      const batchProgress = file.status === 'uploading'
        ? Math.round((file.progress || 0))  // Use the batch's own progress percentage
        : 100;

      const displayProgress = file.status === 'uploading'
        ? (file.statusMessage || `0/${totalFiles} files (${batchProgress}%)`)
        : `${totalFiles} files completed`;

      const batchOperationLabel = file.operationLabel || (
        file.operation === 'delete' ? 'Deleting' :
        file.operation === 'move' ? 'Moving' :
        file.operation === 'copy' ? 'Copying' :
        file.operation === 'undo' ? 'Undoing' :
        file.operation === 'upload' ? 'Uploading' :
        'Processing'
      );

      return (
        <div key={file.id || index} className={`upload-item batch ${file.status}`}>
          <div
            className="upload-item-info batch-header"
            onClick={() => toggleBatchExpand(file.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="batch-title">
              <ChevronRight
                size={16}
                style={{
                  transform: file.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              />
              <span className="file-name">
                {batchOperationLabel} {totalBatches > 1 ? `Batch ${currentBatch}/${totalBatches}` : ''}
              </span>
            </div>
            <span className="file-status small-text" style={{ color: 'var(--subtext-color)' }}>
              {file.status === 'failed' ? 'Failed' : displayProgress}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${file.progress || 0}%`,
                backgroundColor: file.status === 'failed' ? '#f44336' :
                                file.status === 'complete' ? '#4caf50' : '#2196f3'
              }}
            ></div>
          </div>

          {/* Expanded batch details */}
          {file.expanded && file.files && file.files.length > 0 && (
            <div className="batch-details">
              {file.files.map((batchFile, idx) => (
                <div key={idx} className="batch-file-item">
                  <span className="small-text">{formatFilePath(batchFile.name || batchFile.path || batchFile, batchFile.caseName)}</span>
                  {batchFile.status && (
                    <span className={`batch-file-status ${batchFile.status}`}>
                      {batchFile.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {file.error &&
            <div className='error-message'>
              <span>{file.error}</span>
            </div>
          }
        </div>
      );
    }

    // Regular single file upload item
    return (
      <div key={file.id || index} className={`upload-item ${file.status}`}>
        <div className="upload-item-info">
          <span className="file-name">
            {formatFilePath(file.file?.name || file.path || file.name || file.fileName || 'Unknown file', file.caseName)}
          </span>
          <span className="file-status small-text" style={{ color: 'var(--subtext-color)' }}>

            {file.status === 'complete' && file.skipped === true ? '' :
             file.operation === 'rename' ? 'Rename ' :
             file.operation === 'delete' ? 'Delete ' :
             file.operation === 'copy' ? 'Copy ' :
             file.operation === 'move' ? 'Move ' :
             file.operation === 'undo' ? 'Undo ' :
             file.operation === 'merge' ? 'Merge ' :
             file.operation === 'upload' ? 'Upload ' :
             file.operation === 'archive_import' ? (file.archiveImportMode === 'archive_only' ? 'Original files: ' : 'Extracting: ') :
             file.operation === 'transcribe' ? 'Transcription ' : ''}
            {file.status === 'complete' && file.skipped === true ? 'Skipped — already exists' :
             file.operation === 'transcribe' && file.status === 'uploading' ?
              'Transcribing...' :
             file.status === 'uploading' && file.statusMessage ? file.statusMessage :
             file.status === 'uploading' ? `${file.progress || 0}%` :
             file.status === 'complete' ? (file.operation === 'transcribe' ? 'Complete' : 'Completed') :
             file.status === 'cancelled' ? 'Cancelled' :
             'Failed'}
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${file.progress || 0}%`,
              backgroundColor: file.status === 'failed' ? '#f44336' :
                              file.status === 'complete' ? '#4caf50' : '#2196f3'
            }}
          ></div>
        </div>
        {file.error &&
          <div className='error-message'>
            <span>{file.error}</span>
          </div>
        }
        {file.operation === 'archive_import' && file.status === 'failed' && file.archivePasswordRetry && (
          <div className="upload-item-retry-actions">
            <button
              className="action alt"
              onClick={() => {
                setPasswordRetryError('');
                setPasswordRetryTargetId(file.id);
              }}
              type="button"
            >
              <KeyRound aria-hidden="true" size={14} /> Enter ZIP password
            </button>
          </div>
        )}
        {file.operation === 'zip' && file.status === 'uploading' && file.cancelUrl && (
          <div className="upload-item-retry-actions">
            <button
              className="action alt"
              disabled={cancellingUploadIds.has(String(file.id || ''))}
              onClick={() => void cancelBackgroundUpload(file)}
              type="button"
            >
              {cancellingUploadIds.has(String(file.id || '')) ? 'Cancelling...' : 'Cancel ZIP'}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <div className={`upload-tray ${minimized ? 'minimized' : ''}`}>
      <div className="upload-tray-header" onClick={() => setMinimized(!minimized)}>
        <div className="upload-tray-title">
          {inProgressUploads > 0 ? <Clock size={14} /> :
           failedUploads > 0 ? <XCircle size={14} /> : null}
          <span>{headerText}</span>
        </div>
        <div className="upload-tray-actions">
          {finishedUploads > 0 && (
            <Eraser
              size={16}
              onClick={handleClearCompleted}
              title="Clear completed uploads"
            />
          )}
          {minimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          <X
            size={16}
            onClick={(e) => {
              e.stopPropagation();
              dismissedUploadIdsRef.current = new Set(files.map(file => file.id));
              clearAllUploads();
              setVisible(false);
              setUserDismissed(true);
            }}
            title="Clear tray and close"
          />
        </div>
      </div>

      {!minimized && files.length > 0 && (
        <div className="upload-tray-content">
          {files.filter((file) => !['complete', 'failed'].includes(file.status)).map(renderUpload)}
          <UploadOutcomeGroup label="Completed" uploads={files.filter((file) => file.status === 'complete' && file.skipped !== true)} renderUpload={renderUpload} />
          <UploadOutcomeGroup label="Skipped" uploads={files.filter((file) => file.status === 'complete' && file.skipped === true)} renderUpload={renderUpload} />
          <UploadOutcomeGroup label="Failed" uploads={files.filter((file) => file.status === 'failed')} renderUpload={renderUpload} />
        </div>
      )}
    </div>
    {passwordRetryTarget?.archivePasswordRetry && (
      <ArchivePasswordRetryModal
        busy={passwordRetryBusy}
        error={passwordRetryError}
        fileName={formatFilePath(passwordRetryTarget.fileName || passwordRetryTarget.name || '', passwordRetryTarget.caseName)}
        onClose={closePasswordRetry}
        onSubmit={submitArchivePasswordRetry}
      />
    )}
    </>
  );
};
